'use client'

import Link from 'next/link'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import ChevronRight from 'public/icons/arrows/chevron-right.svg'
import { Fragment, useContext, useState } from 'react'
import { GlobalContext } from 'src/components/layout/GlobalContext'
import { buildLocalizedPath, getSiteHomePath } from 'src/_helpers/localizedLinks'

const getHomeLink = (locale, journalDomainRoutes = null) => {
  // Na domenie czasopisma strona główna domyślnego języka jest bez prefiksu.
  if (journalDomainRoutes) {
    return buildLocalizedPath(journalDomainRoutes.HOME, locale) || '/'
  }

  return getSiteHomePath(locale) || '/'
}

export const Breadcrumbs = ({ breadcrumbs = [], current, classCustom = '' }) => {
  const translateEntry = useTranslateEntry()
  const { currentLocale, journalDomainRoutes } = useContext(GlobalContext)
  const [isExpanded, setIsExpanded] = useState(false)

  const breadcrumbItems = [
    { title: translateEntry('HOME_PAGE'), link: getHomeLink(currentLocale, journalDomainRoutes), isCurrent: false },
    ...breadcrumbs
      .filter((breadcrumb) => breadcrumb?.title)
      .map((breadcrumb) => ({
        title: breadcrumb.title,
        link: breadcrumb.link,
        isCurrent: false,
      })),
  ]

  if (current) {
    breadcrumbItems.push({ title: current, link: null, isCurrent: true })
  }

  const shouldCollapse = breadcrumbItems.length > 4
  const getVisibleItems = () => {
    if (!shouldCollapse || isExpanded) {
      return breadcrumbItems
    }
    return [
      breadcrumbItems[0],
      { title: '...', link: null, isCurrent: false, isCollapsed: true },
      ...breadcrumbItems.slice(-2),
    ]
  }

  const visibleItems = getVisibleItems()

  return (
    <nav className={`w-full overflow-hidden pt-10 ${classCustom}`} aria-label={translateEntry('T_BREADCRUMBS')}>
      <ol
        className="flex items-center gap-x-7.5 gap-y-7.5 text-dark-blue-dark-blue2 max-w-full w-auto overflow-hidden"
        itemScope
        itemType="https://schema.org/BreadcrumbList"
      >
        {visibleItems.map((item, index) => (
          <Fragment key={`${item.title}-${index}`}>
            <li
              className="w-auto max-w-full min-w-0 whitespace-nowrap"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              {item.isCollapsed ? (
                <button
                  onClick={() => setIsExpanded(true)}
                  className="buttons-m buttons--bold text-dark-blue-dark-blue2 block px-4 py-4 cursor-pointer hover:opacity-70 transition-opacity"
                  aria-label={translateEntry('T_SHOW_ALL_BREADCRUMBS')}
                >
                  {item.title}
                </button>
              ) : item.link && !item.isCurrent ? (
                <Link
                  itemProp="item"
                  className="buttons-m buttons--bold text-dark-blue-dark-blue2 relative block max-w-full after:content-[''] after:absolute after:left-0 after:bottom-0 after:block after:h-[1.25px] after:w-full after:bg-current btn-link-focus"
                  href={item.link}
                >
                  <span itemProp="name" className="block truncate py-4">
                    {item.title}
                  </span>
                </Link>
              ) : (
                <span
                  itemProp="item"
                  aria-current={item.isCurrent ? 'page' : undefined}
                  className="buttons-m buttons--bold text-dark-blue-dark-blue2 block max-w-full"
                >
                  <span itemProp="name" className="block truncate py-4">
                    {item.title}
                  </span>
                </span>
              )}
              {!item.isCollapsed && <meta itemProp="position" content={index + 1} />}
            </li>
            {index < visibleItems.length - 1 && (
              <ChevronRight aria-hidden="true" className="shrink-0 [&_path]:stroke-current" />
            )}
          </Fragment>
        ))}
      </ol>
    </nav>
  )
}
